"use client"

import { useClientTranslation } from "@/modules/App/components/i18n/i18n.client"
import { Button, Checkbox, Dialog, DialogClose, DialogOverlay, DialogPortal, DialogPrimitiveContent, InputField } from "@dalmatech/ui"
import useForm from "@/shared/hooks/use-form"
import { useMediaQuery } from "@/shared/hooks/use-media-query"
import { CGU, PRIVACY_POLICY_URL } from "@/shared/utils/links"
import { newSubscriptionFlowService } from "@/store/machine"
import { Drawer as DrawerPrimitive } from "vaul"
import { useActor } from "@xstate/react"
import { X } from "lucide-react"
import Lottie from "lottie-react"
import type { LottieRefCurrentProps } from "lottie-react"
import Image from "next/image"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { usePostHog } from "posthog-js/react"
import { useEffect, useRef, useState } from "react"
import MailboxLottie from "../../../../public/lottie/Send.json"
import { Controller, SubmitHandler } from "react-hook-form"
import toast from "react-hot-toast"
import { Trans } from "react-i18next"

import { sendMyQuoteSchema, SendMyQuoteSchema } from "./send-my-quote.schema"
import * as styles from "./send-my-quote.styled"
import { classifyError, useSendMyQuote } from "./use-send-my-quote"

const showFeedbackToast = (variant: "success" | "error", message: string) =>
  toast.custom(
    () => (
      <div className="flex w-[calc(100vw-32px)] max-w-[432px] items-center gap-3 rounded-xl bg-[#21201c] px-4 py-3 shadow-[0px_16px_40px_0px_rgba(26,29,35,0.16)]">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
          <circle cx="12" cy="12" r="12" fill="white" />
          {variant === "success" ? (
            <path d="M7 12.5l3.5 3.5 6.5-7" stroke="#21201c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          ) : (
            <path d="M8 8l8 8M16 8l-8 8" stroke="#21201c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          )}
        </svg>
        <p className="font-buenosAires text-sm font-bold leading-5 tracking-[-0.28px] text-white">{message}</p>
      </div>
    ),
    { position: "bottom-center", duration: 4000 }
  )

export const SendMyQuote = () => {
  const [isOpen, setIsOpen] = useState(false)
  const lottieRef = useRef<LottieRefCurrentProps | null>(null)
  const pathname = usePathname()
  const [state, send] = useActor(newSubscriptionFlowService)
  const { t, i18n } = useClientTranslation("global", { keyPrefix: "send-my-quote" })
  const posthog = usePostHog()
  const { mutate: sendQuote, isPending: isLoading, errorKind } = useSendMyQuote()
  const isDesktop = useMediaQuery("(min-width: 768px)")

  const quoteId = state.context.quote?.meta?.quote_id
  const userId = state.context.quote?.meta?.user_id

  const { handleSubmit, formState, register, control, setValue } = useForm(sendMyQuoteSchema, {
    mode: "onTouched",
    defaultValues: {
      email_sent_to: state.context.quote?.owner?.email ?? undefined,
    },
  })

  useEffect(() => {
    const id = setInterval(() => lottieRef.current?.goToAndPlay(0), 15_000)
    return () => clearInterval(id)
  }, [pathname])

  const emailError = formState.errors.email_sent_to?.message

  const onSubmit: SubmitHandler<SendMyQuoteSchema> = (values) => {
    if (!quoteId || !userId) return

    // DLM-1777: submit-click event, shared name across the non-tested entry points.
    posthog?.capture("cta_clicked", {
      CTA_name: "send_quote_submit_form",
      quote_id: quoteId,
      user_id: userId,
      locale: i18n.language,
    })

    sendQuote(
      {
        quote_id: quoteId,
        user_id: userId,
        email_sent_to: values.email_sent_to,
        locale: i18n.language,
      },
      {
        onSuccess: () => {
          send({ type: "SET_EMAIL_SENT_TO", email: values.email_sent_to })
          posthog?.capture("send_quote_feature", {
            quote_id: quoteId,
            user_id: userId,
            locale: i18n.language,
          })
          showFeedbackToast("success", t("success"))
          setIsOpen(false)
        },
        onError: (error) => {
          // Cooldown keeps its inline banner + disabled CTA; only the generic error surfaces as a toast.
          if (classifyError(error) === "generic") {
            showFeedbackToast("error", t("error.generic"))
          }
        },
      }
    )
  }

  const tcLinks = {
    link1: (
      <Link
        href={new URL(CGU)}
        target="_blank"
        rel="noopener noreferrer"
        className="font-bold text-blurple-500 underline"
      />
    ),
    link2: (
      <Link
        href={new URL(PRIVACY_POLICY_URL)}
        target="_blank"
        rel="noopener noreferrer"
        className="font-bold text-blurple-500 underline"
      />
    ),
  }

  const illustration = (
    <Image src="/img/illus/Mailbox.svg" alt="" aria-hidden width={128} height={128} priority />
  )

  const formContent = (
    <form id="send-my-quote-form" onSubmit={handleSubmit(onSubmit)} className={styles.form}>
      <InputField
        type="email"
        placeholder={t("email-placeholder")}
        error={emailError}
        {...register("email_sent_to")}
      />

      <Controller
        control={control}
        name="tcAccepted"
        render={({ field }) => (
          <Checkbox
            id="send-my-quote-tc"
            checked={field.value === true}
            onCheckedChange={(v) => setValue("tcAccepted", v as true, { shouldValidate: true })}
          >
            <Trans t={t} i18nKey="consent-label" components={tcLinks} />
          </Checkbox>
        )}
      />
    </form>
  )

  const errorBannerEl =
    errorKind === "cooldown" ? (
      <div className={styles.errorBanner} role="alert">
        {t("error.cooldown")}
      </div>
    ) : null

  const submitButton = (
    <Button
      type="submit"
      form="send-my-quote-form"
      size="lg"
      disabled={!formState.isValid || errorKind === "cooldown"}
      isLoading={isLoading}
      isFullWidth
    >
      {t("cta")}
    </Button>
  )

  return (
    <>
      <button
        type="button"
        onClick={() => {
          posthog?.capture("cta_clicked", {
            CTA_name: "send_quote_breadcrumb",
            quote_id: quoteId,
            user_id: userId,
            locale: i18n.language,
          })
          setIsOpen(true)
        }}
        aria-label={t("button-label")}
        className={styles.triggerButton}
      >
        <Lottie lottieRef={lottieRef} animationData={MailboxLottie} loop={false} autoplay={false} className="h-5 w-5" />
      </button>

      {!isDesktop ? (
        <DrawerPrimitive.Root open={isOpen} onOpenChange={setIsOpen}>
          <DrawerPrimitive.Portal>
            <DrawerPrimitive.Overlay className="fixed inset-0 z-30 bg-neutral-600/60" />
            <DrawerPrimitive.Content className="fixed inset-0 z-[60] flex flex-col bg-white">
              <div className="flex shrink-0 items-center border-b border-neutral-200 p-4">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="ml-auto flex h-10 w-10 items-center justify-center rounded-full bg-neutral-100 transition-colors hover:bg-neutral-200"
                >
                  <X className="h-4 w-4 text-neutral-700" aria-hidden />
                  <span className="sr-only">{t("close")}</span>
                </button>
              </div>
              <div className="flex flex-1 flex-col items-center gap-6 overflow-y-auto px-4 py-6">
                {illustration}
                <div className={styles.titleWrapper}>
                  <DrawerPrimitive.Title className={styles.title}>{t("title")}</DrawerPrimitive.Title>
                  <DrawerPrimitive.Description className={styles.description}>
                    {t("description")}
                  </DrawerPrimitive.Description>
                </div>
                {formContent}
              </div>
              <div className="shrink-0 border-t border-neutral-200 bg-white px-4 py-3">
                {errorBannerEl}
                <div className="flex flex-col gap-3">{submitButton}</div>
              </div>
            </DrawerPrimitive.Content>
          </DrawerPrimitive.Portal>
        </DrawerPrimitive.Root>
      ) : (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogPortal>
            <DialogOverlay />
            <DialogPrimitiveContent className={styles.dialogContent}>
              <div className={styles.header}>
                <DialogClose>
                  <button type="button" className={styles.closeButton}>
                    <X className="h-4 w-4 text-neutral-700" aria-hidden />
                    <span className="sr-only">{t("close")}</span>
                  </button>
                </DialogClose>
              </div>

              <div className={styles.body}>
                {illustration}
                <div className={styles.titleWrapper}>
                  <h2 className={styles.title}>{t("title")}</h2>
                  <p className={styles.description}>{t("description")}</p>
                </div>
                {formContent}
                {errorBannerEl}
                {submitButton}
              </div>
            </DialogPrimitiveContent>
          </DialogPortal>
        </Dialog>
      )}
    </>
  )
}
